import { useEffect, useMemo, useRef } from 'react';
import { Box } from '@mui/material';

/**
 * Renders the operator-authored, server-sanitised HTML body of a docs
 * article with the typography/spacing rules used across every reader
 * surface, plus a set of progressive reading enhancements:
 *
 * <ul>
 *   <li>Stable ids on h1–h4 (slugified text, deduped) with hover
 *       anchor links that copy the deep-link URL on click.</li>
 *   <li>h2/h3 outline reported to the parent via
 *       {@code onHeadingsChange} — feeds the "In this article" rail.</li>
 *   <li>Copy button on every {@code <pre>} code block.</li>
 *   <li>Blockquotes that open with <b>Note:</b> / <b>Tip:</b> /
 *       <b>Warning:</b> (also Info/Caution/Important) render as
 *       coloured callout boxes.</li>
 *   <li>External links open in a new tab with
 *       {@code rel="noopener noreferrer"}.</li>
 *   <li>Tables get a horizontal-scroll wrapper so wide reference
 *       tables never break the page on mobile.</li>
 * </ul>
 *
 * <p>Security note: the {@code html} prop is the {@code body} field
 * served by the backend, which Jsoup-cleans against a strict safelist
 * before persist (no scripts, no event handlers, no javascript: URLs).
 * The {@code dangerouslySetInnerHTML} here is defence-in-depth only —
 * the server is the authoritative gate. The DOM transformations below
 * only ever move/annotate existing sanitised nodes.
 */

const slugifyHeading = (s) =>
  String(s || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-+)|(-+$)/g, '')
    .slice(0, 80) || 'section';

/** Callout keyword → visual variant. */
const CALLOUT_VARIANTS = {
  note: 'note',
  info: 'note',
  tip: 'tip',
  hint: 'tip',
  warning: 'warning',
  caution: 'warning',
  important: 'warning',
  danger: 'warning',
};

/**
 * Parse + transform the article HTML once per article. Returns the
 * enhanced markup and the h2/h3 outline. Pure with respect to the
 * input string — safe inside useMemo.
 */
const processHtml = (html) => {
  if (!html) return { html: '', headings: [] };
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const headings = [];
    const seenIds = new Set();

    doc.body.querySelectorAll('h1, h2, h3, h4').forEach((el) => {
      let id = el.id || slugifyHeading(el.textContent);
      let candidate = id;
      let n = 2;
      while (seenIds.has(candidate)) candidate = `${id}-${n++}`;
      id = candidate;
      seenIds.add(id);
      el.id = id;
      const level = Number(el.tagName.charAt(1));
      if (level === 2 || level === 3) {
        headings.push({ id, text: (el.textContent || '').trim(), level });
      }
    });

    doc.body.querySelectorAll('a[href]').forEach((a) => {
      const href = a.getAttribute('href') || '';
      if (/^https?:\/\//i.test(href) && !href.startsWith(window.location.origin)) {
        a.setAttribute('target', '_blank');
        a.setAttribute('rel', 'noopener noreferrer');
      }
    });

    doc.body.querySelectorAll('table').forEach((table) => {
      if (table.parentElement?.classList?.contains('docs-table-wrap')) return;
      const wrap = doc.createElement('div');
      wrap.className = 'docs-table-wrap';
      table.parentNode.insertBefore(wrap, table);
      wrap.appendChild(table);
    });

    doc.body.querySelectorAll('blockquote').forEach((bq) => {
      const opener = (
        bq.querySelector('strong, b')?.textContent ||
        bq.textContent ||
        ''
      )
        .trim()
        .toLowerCase();
      const match = opener.match(/^(note|info|tip|hint|warning|caution|important|danger)\b/);
      if (match) {
        bq.classList.add('docs-callout', `docs-callout-${CALLOUT_VARIANTS[match[1]]}`);
      }
    });

    return { html: doc.body.innerHTML, headings };
  } catch {
    // DOMParser failure — serve the sanitised HTML untouched.
    return { html, headings: [] };
  }
};

const copyText = (text) => {
  if (navigator.clipboard?.writeText) return navigator.clipboard.writeText(text);
  return new Promise((resolve, reject) => {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    try {
      document.execCommand('copy');
      resolve();
    } catch (err) {
      reject(err);
    } finally {
      document.body.removeChild(ta);
    }
  });
};

const DocsArticleBody = ({ html, isDark, onHeadingsChange }) => {
  const rootRef = useRef(null);
  const processed = useMemo(() => processHtml(html), [html]);

  useEffect(() => {
    onHeadingsChange?.(processed.headings);
  }, [processed, onHeadingsChange]);

  // Post-render DOM decoration: copy buttons + heading anchors. The
  // effect re-runs when the article changes (React resets innerHTML,
  // wiping previous decorations).
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    root.querySelectorAll('pre').forEach((pre) => {
      if (pre.querySelector('.docs-copy-btn')) return;
      // Capture the code text BEFORE the button joins the subtree.
      const codeText = (pre.querySelector('code') || pre).textContent.replace(/\n$/, '');
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'docs-copy-btn';
      btn.textContent = 'Copy';
      btn.setAttribute('aria-label', 'Copy code to clipboard');
      btn.addEventListener('click', () => {
        copyText(codeText)
          .then(() => {
            btn.textContent = 'Copied ✓';
            btn.classList.add('copied');
            setTimeout(() => {
              btn.textContent = 'Copy';
              btn.classList.remove('copied');
            }, 1600);
          })
          .catch(() => {});
      });
      pre.appendChild(btn);
    });

    root.querySelectorAll('h1[id], h2[id], h3[id], h4[id]').forEach((h) => {
      if (h.querySelector('.docs-anchor')) return;
      const a = document.createElement('a');
      a.className = 'docs-anchor';
      a.href = `#${h.id}`;
      a.textContent = '#';
      a.setAttribute('aria-label', 'Copy link to this section');
      a.addEventListener('click', (e) => {
        e.preventDefault();
        const url = `${window.location.origin}${window.location.pathname}#${h.id}`;
        copyText(url).catch(() => {});
        window.history.replaceState(null, '', `#${h.id}`);
        a.classList.add('copied');
        setTimeout(() => a.classList.remove('copied'), 1200);
      });
      h.appendChild(a);
    });

    // Deep link: land on the anchored section when the page loads with
    // a hash (the sticky header offset is handled by scroll-margin-top).
    if (window.location.hash) {
      const target = document.getElementById(
        decodeURIComponent(window.location.hash.slice(1))
      );
      if (target) target.scrollIntoView({ block: 'start' });
    }
  }, [processed.html]);

  return (
    <Box
      ref={rootRef}
      sx={{
        fontSize: '1rem',
        lineHeight: 1.75,
        wordBreak: 'break-word',
        '& > :first-of-type': { mt: 0 },
        '& h1, & h2, & h3, & h4': {
          position: 'relative',
          scrollMarginTop: '96px',
          letterSpacing: '-0.01em',
          '&:hover .docs-anchor': { opacity: 1 },
        },
        '& h1': { fontSize: '1.9rem', fontWeight: 800, mt: 5, mb: 2 },
        '& h2': {
          fontSize: '1.45rem',
          fontWeight: 700,
          mt: 5,
          mb: 1.5,
          pb: 0.75,
          borderBottom: '1px solid',
          borderColor: 'divider',
        },
        '& h3': { fontSize: '1.15rem', fontWeight: 700, mt: 3.5, mb: 1 },
        '& h4': { fontSize: '1rem', fontWeight: 700, mt: 3, mb: 1 },
        '& .docs-anchor': {
          position: 'absolute',
          ml: 1,
          px: 0.5,
          opacity: 0,
          textDecoration: 'none',
          color: 'primary.main',
          fontWeight: 400,
          transition: 'opacity 120ms',
          '&.copied': { color: 'success.main' },
        },
        '& p': { mb: 2 },
        '& a': {
          color: 'primary.main',
          textDecoration: 'underline',
          textDecorationColor: isDark ? 'rgba(110,168,254,0.4)' : 'rgba(9,105,218,0.35)',
          textUnderlineOffset: '2px',
          '&:hover': { textDecorationColor: 'currentColor' },
        },
        '& code': {
          backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
          px: 0.6,
          py: 0.1,
          borderRadius: 0.5,
          fontFamily: '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
          fontSize: '0.875em',
        },
        '& kbd': {
          display: 'inline-block',
          px: 0.75,
          py: 0.1,
          borderRadius: 0.75,
          border: '1px solid',
          borderColor: 'divider',
          borderBottomWidth: 2,
          backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)',
          fontFamily: '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
          fontSize: '0.8em',
        },
        '& mark': {
          backgroundColor: isDark ? 'rgba(210,153,34,0.38)' : 'rgba(255,213,0,0.45)',
          color: 'inherit',
          borderRadius: '2px',
          px: 0.25,
        },
        '& pre': {
          position: 'relative',
          backgroundColor: isDark ? '#0d1117' : '#f6f8fa',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1.5,
          p: 2,
          pr: 9,
          overflowX: 'auto',
          fontFamily: '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
          fontSize: '0.875em',
          lineHeight: 1.6,
          my: 2.5,
          '& code': { backgroundColor: 'transparent', p: 0, fontSize: '1em' },
          '&:hover .docs-copy-btn': { opacity: 1 },
        },
        '& .docs-copy-btn': {
          position: 'absolute',
          top: 8,
          right: 8,
          px: 1,
          py: 0.4,
          fontSize: '0.72rem',
          fontWeight: 600,
          fontFamily: 'inherit',
          lineHeight: 1.4,
          cursor: 'pointer',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          color: 'text.secondary',
          backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : '#ffffff',
          opacity: { xs: 1, md: 0 },
          transition: 'opacity 120ms, color 120ms',
          '&:hover': { color: 'primary.main', borderColor: 'primary.main' },
          '&.copied': { color: 'success.main', borderColor: 'success.main', opacity: 1 },
        },
        '& blockquote': {
          borderLeft: '3px solid',
          borderColor: 'primary.main',
          m: 0,
          my: 2.5,
          pl: 2,
          py: 0.5,
          color: 'text.secondary',
          fontStyle: 'italic',
        },
        '& blockquote.docs-callout': {
          fontStyle: 'normal',
          color: 'text.primary',
          borderRadius: 1.5,
          border: '1px solid',
          borderLeft: '3px solid',
          px: 2,
          py: 1.5,
          '& p': { mb: 1, '&:last-of-type': { mb: 0 } },
        },
        '& blockquote.docs-callout-note': {
          borderColor: isDark ? 'rgba(110,168,254,0.4)' : 'rgba(9,105,218,0.35)',
          borderLeftColor: isDark ? '#6ea8fe' : '#0969da',
          backgroundColor: isDark ? 'rgba(110,168,254,0.08)' : 'rgba(9,105,218,0.05)',
        },
        '& blockquote.docs-callout-tip': {
          borderColor: isDark ? 'rgba(63,185,80,0.4)' : 'rgba(26,127,55,0.35)',
          borderLeftColor: isDark ? '#3fb950' : '#1a7f37',
          backgroundColor: isDark ? 'rgba(63,185,80,0.08)' : 'rgba(26,127,55,0.05)',
        },
        '& blockquote.docs-callout-warning': {
          borderColor: isDark ? 'rgba(210,153,34,0.45)' : 'rgba(154,103,0,0.35)',
          borderLeftColor: isDark ? '#d29922' : '#9a6700',
          backgroundColor: isDark ? 'rgba(210,153,34,0.08)' : 'rgba(154,103,0,0.05)',
        },
        '& ul, & ol': { pl: 4, mb: 2 },
        '& li': { mb: 0.5, '& > ul, & > ol': { mt: 0.5, mb: 0 } },
        '& .docs-table-wrap': {
          overflowX: 'auto',
          my: 2.5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1.5,
        },
        '& table': {
          borderCollapse: 'collapse',
          width: '100%',
          fontSize: '0.925em',
          '& th, & td': {
            border: 0,
            borderBottom: '1px solid',
            borderColor: 'divider',
            px: 1.5,
            py: 1,
            verticalAlign: 'top',
            textAlign: 'left',
          },
          '& tr:last-of-type td': { borderBottom: 0 },
          '& th': {
            backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
            fontWeight: 700,
            whiteSpace: 'nowrap',
          },
          '& tbody tr:nth-of-type(even)': {
            backgroundColor: isDark ? 'rgba(255,255,255,0.015)' : 'rgba(0,0,0,0.012)',
          },
        },
        '& img': {
          maxWidth: '100%',
          height: 'auto',
          borderRadius: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          my: 2,
        },
        '& hr': { border: 0, borderTop: '1px solid', borderColor: 'divider', my: 4 },
        '@media print': {
          fontSize: '11pt',
          '& .docs-copy-btn, & .docs-anchor': { display: 'none' },
          '& pre': { whiteSpace: 'pre-wrap', overflowX: 'visible' },
          '& a': { color: 'inherit' },
        },
      }}
      dangerouslySetInnerHTML={{ __html: processed.html }}
    />
  );
};

export default DocsArticleBody;
